<template>
  <teleport to="#modal-container">
    <transition name="fade">
      <div
        v-if="isOpen"
        class="tf-centered-modal"
        role="dialog"
        aria-modal="true"
        @click.self="close"
      >
        <div class="tf-centered-modal__content" @click.stop>
          <div
            class="tf-centered-modal__header d-flex justify-content-between align-items-start"
          >
            <h2 class="title-4 text-primary-black mb-0 tf-centered-modal__title">
              {{ t('reservations.reserveActionsTitle') }}
            </h2>
            <CloseButton @close="close" />
          </div>

          <div class="tf-centered-modal__body">
            <div v-if="depositAction" class="reserve-actions-group">
              <button
                type="button"
                class="reserve-action-row reserve-action-row--featured"
                @click="onSelect(depositAction)"
              >
                <span
                  class="reserve-action-row__badge reserve-action-row__badge--deposit"
                  aria-hidden="true"
                >
                  <ReservationIcon class="reserve-action-row__icon" />
                </span>
                <span class="reserve-action-row__content">
                  <span class="reserve-action-row__title">{{
                    depositAction.title
                  }}</span>
                  <span
                    v-if="depositAction.amount"
                    class="reserve-action-row__amount"
                  >
                    {{ depositAction.amount }}
                  </span>
                </span>
                <ChevronRight class="reserve-action-row__chevron" />
              </button>
            </div>

            <div v-if="paymentActions.length" class="reserve-actions-group">
              <p
                v-if="paymentActions.length > 1 || depositAction"
                class="reserve-actions-group__label caption text-grey-700 mb-2"
              >
                {{ t('reservations.reserveActionsPaymentsSection') }}
              </p>
              <ul class="reserve-action-list list-unstyled mb-0">
                <li
                  v-for="action in paymentActions"
                  :key="action.id"
                  class="reserve-action-list__item"
                >
                  <button
                    type="button"
                    class="reserve-action-row"
                    @click="onSelect(action)"
                  >
                    <span
                      class="reserve-action-row__badge"
                      :class="{
                        'reserve-action-row__badge--numbered':
                          action.instalmentNumber != null,
                      }"
                      aria-hidden="true"
                    >
                      <span
                        v-if="action.instalmentNumber != null"
                        class="reserve-action-row__number"
                      >
                        {{ action.instalmentNumber }}
                      </span>
                      <span v-else class="reserve-action-row__symbol">€</span>
                    </span>
                    <span class="reserve-action-row__content">
                      <span class="reserve-action-row__title">{{
                        action.title
                      }}</span>
                      <span
                        v-if="action.amount"
                        class="reserve-action-row__amount"
                      >
                        {{ action.amount }}
                      </span>
                    </span>
                    <ChevronRight class="reserve-action-row__chevron" />
                  </button>
                </li>
              </ul>
            </div>

            <div v-if="balanceAction" class="reserve-actions-group">
              <button
                type="button"
                class="reserve-action-row reserve-action-row--balance"
                @click="onSelect(balanceAction)"
              >
                <span
                  class="reserve-action-row__badge reserve-action-row__badge--balance"
                  aria-hidden="true"
                >
                  <span class="reserve-action-row__symbol">€</span>
                </span>
                <span class="reserve-action-row__content">
                  <span class="reserve-action-row__title">{{
                    balanceAction.title
                  }}</span>
                </span>
                <ChevronRight class="reserve-action-row__chevron" />
              </button>
            </div>

            <button
              type="button"
              class="reserve-actions-cancel link-default w-100 mt-3"
              @click="close"
            >
              {{ t('reservations.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CloseButton from '@/components/UI/CloseButton.vue'
import ChevronRight from '@/assets/ChevronRight.vue'
import ReservationIcon from '@/assets/ReservationIcon.vue'
import type { ReserveAction } from '@/utils/bookingFunnelNavigation'

const props = defineProps({
  actions: {
    type: Array as () => ReserveAction[],
    required: true,
  },
})

const emit = defineEmits<{
  select: [action: ReserveAction]
}>()

const { t } = useI18n()
const isOpen = ref(false)

const depositAction = computed(() =>
  props.actions.find(action => action.kind === 'options')
)
const paymentActions = computed(() =>
  props.actions.filter(action => action.kind === 'payment')
)
const balanceAction = computed(() =>
  props.actions.find(action => action.kind === 'balance')
)

const open = (): void => {
  isOpen.value = true
  document.body.style.overflow = 'hidden'
}

const close = (): void => {
  isOpen.value = false
  document.body.style.overflow = ''
}

const onSelect = (action: ReserveAction): void => {
  close()
  emit('select', action)
}

defineExpose({ open, close })
</script>

<style scoped>
.tf-centered-modal {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
}

.tf-centered-modal__content {
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: var(--primary-white, #fff);
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

@media (min-width: 768px) {
  .tf-centered-modal__content {
    padding: 28px 32px 32px;
  }
}

.tf-centered-modal__header {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.tf-centered-modal__title {
  padding-right: 12px;
}

.reserve-actions-group + .reserve-actions-group {
  margin-top: 16px;
}

.reserve-actions-group__label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.reserve-action-list {
  border: 1px solid var(--gray-200, #e5e7eb);
  border-radius: var(--border-radius-default, 8px);
  overflow: hidden;
  background: var(--primary-white, #fff);
}

.reserve-action-list__item + .reserve-action-list__item {
  border-top: 1px solid var(--gray-200, #e5e7eb);
}

.reserve-action-row {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.reserve-action-row:hover,
.reserve-action-row:focus-visible {
  background: var(--blue-100, #f0f6fc);
  outline: none;
}

.reserve-action-row--featured {
  border: 1px solid var(--primary-blue, #0056b3);
  border-radius: var(--border-radius-default, 8px);
  background: var(--blue-100, #f0f6fc);
}

.reserve-action-row--featured:hover,
.reserve-action-row--featured:focus-visible {
  background: #e3eef9;
}

.reserve-action-row--balance {
  border: 1px dashed var(--gray-200, #e5e7eb);
  border-radius: var(--border-radius-default, 8px);
}

.reserve-action-row__badge {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--grey-200, #eef1f4);
  color: var(--primary-blue, #0056b3);
}

.reserve-action-row__badge--deposit {
  background: var(--primary-blue, #0056b3);
  color: var(--primary-white, #fff);
}

.reserve-action-row__badge--deposit :deep(.reserve-action-row__icon) {
  width: 22px;
  height: 22px;
}

.reserve-action-row__badge--numbered {
  background: var(--primary-blue, #0056b3);
  color: var(--primary-white, #fff);
  font-weight: 700;
  font-size: 1rem;
}

.reserve-action-row__badge--balance {
  background: var(--blue-100, #f0f6fc);
}

.reserve-action-row__symbol {
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1;
}

.reserve-action-row__number {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
}

.reserve-action-row__content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.reserve-action-row__title {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 0.9375rem;
  line-height: 1.35;
  color: var(--primary-black, #0d141b);
  text-align: left;
}

.reserve-action-row__subtitle {
  display: block;
  margin-top: 4px;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--grey-700, #5c6b7a);
}

.reserve-action-row__amount {
  flex-shrink: 0;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--primary-black, #0d141b);
  text-align: right;
  white-space: nowrap;
}

.reserve-action-row__chevron {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.reserve-actions-cancel {
  display: block;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: center;
  font-size: 0.9375rem;
}

.reserve-actions-cancel:hover {
  text-decoration: underline;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
